import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { Viewer } from '@photo-sphere-viewer/core';
import type { PanoData, TextureData } from '@photo-sphere-viewer/core';
import type { Texture } from 'three';
import { EquirectangularVideoAdapter } from '@photo-sphere-viewer/equirectangular-video-adapter';
import { VideoPlugin } from '@photo-sphere-viewer/video-plugin';
import { SettingsPlugin } from '@photo-sphere-viewer/settings-plugin';
import { ResolutionPlugin } from '@photo-sphere-viewer/resolution-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/video-plugin/index.css';
import '@photo-sphere-viewer/settings-plugin/index.css';
import { SHARED_LINK_QUERY } from '../../graphql/queries';
import { registerMediaRate, trackMedia } from '../../utils/viewerUsage';
import {
  deliveryRequest,
  formatDuration,
  pickInitialRendition,
  renditionOptionLabel,
  stereoEyeCrop,
  type Video360Delivery,
  type Video360Rendition,
} from '../../utils/video360';
import styles from './Video360Viewer.module.css';

const FILE_QUERY = gql`
  query Video360File($id: ID!) {
    file(id: $id) {
      id
      originalName
      fileSize
      status
    }
  }
`;

const POLL_MS = 5000;
// Delivery URLs are signed for an hour: refresh them well before that so a
// quality switch never loads a stale one, and treat a media error on a
// descriptor older than this as an expired signature worth one reload.
const REFRESH_EVERY_MS = 45 * 60 * 1000;
const STALE_AFTER_MS = 30 * 60 * 1000;

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// A stereo frame holds two equirects, so its own aspect is not 2:1 — declare
// the full sphere explicitly and let the texture crop pick one eye.
const fullSphere = (video: HTMLVideoElement): PanoData => {
  const fullHeight = Math.round(video.videoWidth / 2);
  return {
    fullWidth: video.videoWidth,
    fullHeight,
    croppedWidth: video.videoWidth,
    croppedHeight: fullHeight,
    croppedX: 0,
    croppedY: 0,
  };
};

type Stage = 'checking' | 'processing' | 'ready' | 'failed';
// The resolution plugin keeps these exact objects, so a refreshed url and its
// issue time written in place reach every later quality switch.
interface SignedPanorama { source: string; issuedAt: number; data?: (video: HTMLVideoElement) => PanoData }
interface Failure { title: string; detail: string }

const Video360Viewer = () => {
  const { fileId, token: shareToken } = useParams<{ projectId?: string; fileId: string; token?: string }>();
  const isShare = !!shareToken;
  const navigate = useNavigate();

  const psvEl = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const [stage, setStage] = useState<Stage>('checking');
  const [failure, setFailure] = useState<Failure | null>(null);
  const [progress, setProgress] = useState<{ stage?: string; pct?: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [delivery, setDelivery] = useState<Video360Delivery | null>(null);
  const deliveryRef = useRef<Video360Delivery | null>(null);
  const fetchedAtRef = useRef(0);
  const [quality, setQuality] = useState<Video360Rendition | null>(null);

  const { data } = useQuery(FILE_QUERY, { variables: { id: fileId }, skip: !fileId || isShare });
  const { data: shareData } = useQuery(SHARED_LINK_QUERY, { variables: { token: shareToken }, skip: !isShare });
  const fileInfo = isShare
    ? shareData?.sharedLink?.files?.find((f: { id: string }) => f.id === fileId)
    : data?.file;
  const fileName: string | undefined = fileInfo?.originalName;

  const goBack = () => {
    if (isShare) navigate(`/share/${shareToken}`);
    else navigate(-1);
  };

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const { url, headers } = deliveryRequest(fileId, shareToken);
        const res = await fetch(url, { headers });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const desc: Video360Delivery = await res.json();
        if (cancelled) return;
        if (desc.status === 'ready') {
          if (!desc.renditions?.length) throw new Error('No playable rendition was produced');
          deliveryRef.current = desc;
          fetchedAtRef.current = Date.now();
          setDelivery(desc);
          setStage('ready');
          return;
        }
        if (desc.status === 'error') {
          setFailure({ title: 'Conversion failed', detail: 'The video could not be converted — try re-uploading the file' });
          setStage('failed');
          return;
        }
        setUploading(desc.status === 'uploading');
        setProgress(desc.progress ?? null);
        setStage('processing');
        timer = window.setTimeout(poll, POLL_MS);
      } catch (err) {
        if (!cancelled) {
          setFailure({ title: 'Failed to load', detail: err instanceof Error ? err.message : 'Failed to load' });
          setStage('failed');
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [fileId, shareToken]);

  useEffect(() => {
    const el = psvEl.current;
    const first = deliveryRef.current;
    if (stage !== 'ready' || !first || !el || !fileId) return;
    const renditions = first.renditions ?? [];
    const initial = pickInitialRendition(renditions, el.clientWidth || window.innerWidth, window.devicePixelRatio || 1);
    if (!initial) return;

    const crop = stereoEyeCrop(first.stereo);
    const issuedAt = fetchedAtRef.current;
    const panoramaOf = new Map<string, SignedPanorama>(renditions.map((r) => [r.name, crop
      ? { source: r.url, issuedAt, data: fullSphere }
      : { source: r.url, issuedAt }]));
    const byName = new Map(renditions.map((r) => [r.name, r]));
    // The usage meter prices what the <video> buffered at each rendition's bitrate.
    const meterRates = (d: Video360Delivery) => {
      for (const r of d.renditions ?? []) if (r.bytes && d.duration) registerMediaRate(r.url, r.bytes / d.duration);
    };
    meterRates(first);
    const heading = typeof first.heading === 'number' && Number.isFinite(first.heading) ? first.heading : 0;

    const viewer = new Viewer({
      container: el,
      adapter: [EquirectangularVideoAdapter, { autoplay: false, muted: false }],
      panorama: panoramaOf.get(initial.name),
      defaultYaw: `${heading}deg`,
      navbar: ['videoPlay', 'videoVolume', 'videoTime', 'caption', 'settings', 'fullscreen'],
      plugins: [
        [VideoPlugin, { progressbar: true, bigbutton: true }],
        SettingsPlugin,
        [ResolutionPlugin, {
          resolutions: renditions.map((r) => ({
            id: r.name,
            label: renditionOptionLabel(r),
            panorama: panoramaOf.get(r.name),
          })),
        }],
      ],
    });
    viewerRef.current = viewer;

    const video = viewer.getPlugin<VideoPlugin>(VideoPlugin);
    const resolution = viewer.getPlugin<ResolutionPlugin>(ResolutionPlugin);
    let lastTime = 0;
    let current = panoramaOf.get(initial.name);
    let activeMedia: HTMLVideoElement | undefined;
    let activeIssuedAt = issuedAt;
    let disposed = false;
    let recovering = false;

    const refreshUrls = async (): Promise<boolean> => {
      const { url, headers } = deliveryRequest(fileId, shareToken);
      const res = await fetch(url, { headers });
      if (!res.ok) return false;
      const fresh: Video360Delivery = await res.json();
      if (fresh.status !== 'ready' || disposed) return false;
      const now = Date.now();
      for (const r of fresh.renditions ?? []) {
        const p = panoramaOf.get(r.name);
        if (p) { p.source = r.url; p.issuedAt = now; }
      }
      meterRates(fresh);
      deliveryRef.current = fresh;
      fetchedAtRef.current = Date.now();
      return true;
    };
    const fail = (detail: string) => {
      if (disposed) return;
      setFailure({ title: 'Failed to load', detail });
      setStage('failed');
    };
    // An expired signature shows up as a media error on the <video>, or as a
    // panorama-error when a switch loads a stale URL. Reloading the current
    // panorama swaps in the fresh URL; the adapter carries pause and volume
    // across and the panorama-loaded handler restores the time. The gate is the
    // age of the URL that failed, not of the descriptor: the periodic refresh
    // never touches a playing element.
    const recover = async (detail: string, failedIssuedAt: number) => {
      if (recovering || !current || Date.now() - failedIssuedAt < STALE_AFTER_MS) {
        fail(detail);
        return;
      }
      recovering = true;
      try {
        if (!(await refreshUrls())) { fail(detail); return; }
        await viewer.setPanorama(current, { transition: false, showLoader: false });
      } catch {
        fail(detail);
      } finally {
        recovering = false;
      }
    };

    video.addEventListener('progress', (e) => {
      lastTime = e.time;
    });
    // The adapter carries the play position across a quality switch only when
    // both renditions report the exact same duration, which separately encoded
    // files need not do — re-seek ourselves whenever it was lost.
    viewer.addEventListener('panorama-loaded', (e) => {
      const { texture, panorama } = e.data as TextureData<Texture, SignedPanorama>;
      if (crop) {
        texture.repeat.set(crop.repeat[0], crop.repeat[1]);
        texture.offset.set(crop.offset[0], crop.offset[1]);
      }
      activeIssuedAt = panorama?.issuedAt ?? fetchedAtRef.current;
      const media = texture.image as HTMLVideoElement | undefined;
      if (media && typeof media.addEventListener === 'function') {
        // Kept out of the page by the adapter, so the usage meter cannot find it alone.
        trackMedia(media);
        const previous = activeMedia;
        activeMedia = media;
        // The adapter pauses and detaches the replaced element but leaves its
        // src, so it would keep buffering and could still raise an error.
        if (previous && previous !== media) {
          previous.removeAttribute('src');
          previous.load();
        }
        media.addEventListener('error', () => {
          if (disposed || media !== activeMedia) return;
          void recover('The video stream could not be loaded', activeIssuedAt);
        }, { once: true });
      }
      if (lastTime > 0.5 && Math.abs(video.getTime() - lastTime) > 0.5) video.setTime(lastTime);
    });
    viewer.addEventListener('panorama-error', (e) => {
      const failed = (e as { panorama?: SignedPanorama }).panorama;
      void recover(e.error?.message || 'The video could not be loaded', failed?.issuedAt ?? fetchedAtRef.current);
    });
    resolution.addEventListener('resolution-changed', (e) => {
      current = panoramaOf.get(e.resolutionId) ?? current;
      setQuality(byName.get(e.resolutionId) ?? null);
    });
    setQuality(initial);
    const refreshTimer = window.setInterval(() => { refreshUrls().catch(() => {}); }, REFRESH_EVERY_MS);

    return () => {
      disposed = true;
      window.clearInterval(refreshTimer);
      viewerRef.current = null;
      viewer.destroy();
    };
  }, [stage, fileId, shareToken]);

  useEffect(() => {
    if (fileName) viewerRef.current?.setOption('caption', fileName);
  }, [fileName, stage]);

  const pct = typeof progress?.pct === 'number' ? Math.min(100, Math.max(0, progress.pct)) : null;
  const posterStyle = delivery?.poster ? { backgroundImage: `url("${delivery.poster}")` } : undefined;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={goBack}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
            Back
          </button>
          <div className={styles.divider} />
          <div className={styles.fileInfo}>
            <span className={`material-symbols-outlined ${styles.fileIcon}`}>panorama_photosphere</span>
            <span className={styles.fileName}>{fileName ?? 'Loading…'}</span>
            {fileInfo && (
              <span className={styles.fileMeta}>{formatSize(fileInfo.fileSize)}</span>
            )}
          </div>
        </div>
        <div className={styles.headerRight}>
          {typeof delivery?.duration === 'number' && (
            <span className={styles.metaChip}>
              <span className={`material-symbols-outlined ${styles.metaChipIcon}`}>schedule</span>
              {formatDuration(delivery.duration)}
            </span>
          )}
          {quality && (
            <span className={styles.metaChip}>
              <span className={`material-symbols-outlined ${styles.metaChipIcon}`}>high_quality</span>
              {renditionOptionLabel(quality)}
            </span>
          )}
          {delivery?.stereo && delivery.stereo !== 'mono' && (
            <span className={styles.metaChip}>
              <span className={`material-symbols-outlined ${styles.metaChipIcon}`}>view_in_ar</span>
              Stereo {delivery.stereo} · showing one eye
            </span>
          )}
          {delivery && delivery.hasAudio === false && (
            <span className={styles.metaChip}>
              <span className={`material-symbols-outlined ${styles.metaChipIcon}`}>volume_off</span>
              No audio
            </span>
          )}
        </div>
      </header>

      <div className={styles.stage} style={posterStyle}>
        <div ref={psvEl} className={styles.psv} />

        {stage === 'checking' && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 34, color: '#0061ff' }}>panorama_photosphere</span>
            </div>
            <p className={styles.stateTitle}>Checking file…</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
          </div>
        )}

        {stage === 'processing' && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 34, color: '#0061ff' }}>autorenew</span>
            </div>
            <p className={styles.stateTitle}>{uploading ? 'Finishing upload…' : 'Converting…'}</p>
            <p className={styles.stateSub}>
              {progress?.stage ?? (uploading ? 'Waiting for the upload to complete' : 'Queued')}
              {pct !== null ? ` — ${Math.round(pct)}%` : ''}
            </p>
            <div className={styles.progressBar}>
              {pct !== null
                ? <div className={styles.progressFillKnown} style={{ width: `${pct}%` }} />
                : <div className={styles.progressFill} />}
            </div>
            <p className={styles.stateHint}>
              Every quality level is encoded from your upload. This page updates automatically.
            </p>
          </div>
        )}

        {stage === 'failed' && (
          <div className={styles.stateOverlay}>
            <div className={styles.stateIcon}>
              <span className="material-symbols-outlined" style={{ fontSize: 34, color: '#f87171' }}>error</span>
            </div>
            <p className={styles.stateTitle}>{failure?.title ?? 'Failed to load'}</p>
            {failure?.detail && <div className={styles.errorBox}>{failure.detail}</div>}
            <div className={styles.actionRow}>
              <button className={styles.actionButton} onClick={goBack}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>arrow_back</span>
                Back
              </button>
              <button className={styles.actionButton} onClick={() => window.location.reload()}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span>
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Video360Viewer;
